/**
 * useUserRole — single source-of-truth resolver for the current user's role.
 *
 * This is the ONE place the DIoT portal reconciles the app's several role
 * signals into the NUMERIC `central_role_id` scheme the design uses. Guards
 * (e.g. `RequireRole`) and pages consume this resolver instead of scattering
 * inline `Constants.userRole === Constants.RPC_ADMIN || ...` checks.
 *
 * Role sources reconciled (in preference order):
 *   1. `state.auth.userData.Data.central_role_id` — the numeric role id from the
 *      Central `/Central/user/{id}` response (stored verbatim via `setUserData`;
 *      note it lives under `.Data`, mirroring how `App.jsx` reads it). This is
 *      the cleanest numeric source and matches the ids the backend `authorize()`
 *      enforces.
 *   2. `state.Routes.userRoleName` — the role NAME string (set from
 *      `response.Data.role_name`, mirrored onto `Constants.userRole`). Existing
 *      pages (ValueDashboard, EDNPage, AssetDashboard) gate on this, so it is a
 *      reliable fallback when the numeric id is unavailable; we map name→number.
 *
 * Loaded state: `state.Routes.applicationIdsLoad` — set `true` in the same thunk
 * that populates `userRoleName` and `userData`, so it is a faithful "role is
 * resolved" signal (same flag ValueDashboard uses to avoid flash-redirects).
 *
 * @module useUserRole
 */

import { useSelector, shallowEqual } from "react-redux";
import { Constants } from "../../utils/Constants";

/**
 * Numeric role id -> role NAME.
 * Source of truth: the DuoLift `DUO_USER_ROLE` table
 * (RpcAdmin=1, CustomerAdmin=3, LocationAdmin=5). NOTE: the earlier spec
 * assumed {5,7,8}, but 7/8 are actually CustomerUser/LocationUser — the admin
 * ids are {1,3,5}.
 */
export const ROLE_ID_TO_NAME = {
  1: Constants.RPC_ADMIN, // "RpcAdmin"
  3: Constants.CUSTOMER_ADMIN, // "CustomerAdmin"
  5: Constants.LOCATION_ADMIN, // "LocationAdmin"
};

/**
 * Role NAME -> numeric `central_role_id` (inverse of ROLE_ID_TO_NAME).
 */
export const ROLE_NAME_TO_ID = Object.entries(ROLE_ID_TO_NAME).reduce(
  (acc, [id, name]) => {
    acc[name] = Number(id);
    return acc;
  },
  {}
);

/**
 * Plain selector: resolve the current user's role from redux state and
 * normalize it to a numeric role id. Usable outside React (thunks, tests) as
 * well as by the `useUserRole` hook.
 *
 * @param {object} state - redux root state
 * @returns {{ roleId: number|null, roleName: string|null, loaded: boolean }}
 */
export function selectUserRole(state) {
  // The role can arrive from either flow:
  //  - `checkAccess` (nav / auth) stores the parsed `/auth/application/7` payload
  //    at `state.auth.userData.Data` (carries `role_name`, `applications`), OR
  //  - the Dashboard `getUserRole` flow sets `state.Routes.userRoleName` +
  //    `applicationIdsLoad` and stores `central_role_id` on `userData.Data`.
  // We reconcile all of them so the guard resolves regardless of entry path.
  const userDataDetail = state?.auth?.userData?.Data;
  // Candidate role NAMEs, in order: the auth-app role the nav trusts
  // (userData.Data.role_name) and the Dashboard flow's Routes.userRoleName.
  const authRoleName = userDataDetail?.role_name || null;
  const routesRoleName = state?.Routes?.userRoleName || null;
  const roleName = authRoleName || routesRoleName;
  const centralRoleId = userDataDetail?.central_role_id;

  const numericFromId = Number(centralRoleId);
  let roleId = null;

  if (
    centralRoleId !== undefined &&
    centralRoleId !== null &&
    centralRoleId !== "" &&
    Number.isFinite(numericFromId)
  ) {
    // Prefer the numeric source of truth when present.
    roleId = numericFromId;
  } else {
    // Try each candidate NAME and use the first that maps to a known id, so a
    // non-mapping value in one source never shadows a valid one in another.
    for (const name of [authRoleName, routesRoleName]) {
      if (name && ROLE_NAME_TO_ID[name] !== undefined) {
        roleId = ROLE_NAME_TO_ID[name];
        break;
      }
    }
  }

  // "Loaded" = we have ANY authoritative role signal, not just the Dashboard
  // flow's applicationIdsLoad flag (which isn't set when landing on the portal
  // directly). Once checkAccess populates userData.Data — or a role resolves —
  // the guard can decide instead of rendering null forever (blank page).
  const loaded =
    Boolean(state?.Routes?.applicationIdsLoad) ||
    Boolean(userDataDetail) ||
    roleId !== null;

  return { roleId, roleName, loaded };
}

/**
 * React hook wrapping `selectUserRole`. Returns the normalized numeric role id,
 * the raw role name, and whether the role has finished loading.
 *
 * @returns {{ roleId: number|null, roleName: string|null, loaded: boolean }}
 */
export default function useUserRole() {
  // shallowEqual so the fresh {roleId, roleName, loaded} object each call is
  // treated as equal when the fields are unchanged (avoids the react-redux
  // "selector returned a different result" re-render warning).
  return useSelector(selectUserRole, shallowEqual);
}
